import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ContentNode } from '@amzn/awscat-pattern-library-service-model';
import { TiptapOutput, TiptapOutputType } from '@amzn/awscat-editor';
import { isURL } from 'class-validator';
import _ from 'lodash';
import { findNodeById, transformTiptapHtml } from '../../api/ContentUtil';
import { deepCloneContent } from '../../model/CloneContent';
import { useContentEditorService } from '../../contexts/ContentEditorServiceContext';
import { useMplServiceClient } from '../../contexts/MplServiceClientContext';
import { usePendingFlushCoordinator } from '../../contexts/PendingFlushCoordinatorContext';
import { sanitizeCodeBlock, fetchImageInBase64Format } from '../../editor/utils';
import { log } from '../../loggers/Logger';
import { ContentSectionKey } from '../../model/ContentSchemaSection';
import { InlineTextFilter } from '../../services/InlineTextOperations';
import { useEntities } from '../v2-hooks/useEntities';
import { SaveDraftError, ServiceCallError } from '../../services/Errors';

const ALLOWED_VIDEO_SOURCES = ['youtube.com/watch'];

/**
 * Idle delay before staging a content edit. Micro-optimization for smoother
 * typing: each keystroke fires `onChange` from Tiptap, and naively staging on
 * every keystroke runs `deepCloneContent` over the entire content tree plus a
 * `findNodeById` walk — wasted CPU during a typing burst. AutoSavingService
 * already debounces the working-copy write by 3s, so a slightly shorter delay
 * here lets the service still see fresh content well before its own timer.
 */
const STAGE_EDIT_DEBOUNCE_MS = 2000;

/** State and actions exposed by the SectionEditorControl ViewModel. */
export interface SectionEditorControlViewModel {
  /** Initial content for the TiptapEditor (computed once, stable reference). */
  readonly initialContent: TiptapOutputType;
  /** Whether the editor is readonly. */
  readonly isReadonly: boolean;
  /** Entity dictionary for @-mention resolution. */
  readonly entities: Record<string, any>;
  /** What inline edits to display in this section, represented by the filter */
  readonly inlineTextFilter: InlineTextFilter;
  /** Current validation error message, or undefined if none. */
  readonly validationError: string | undefined;
  /** Clears the current validation error. */
  readonly clearValidationError: () => void;
  /** Called when editor content changes. Stages edit to ContentEditorService. */
  readonly handleContentChanged: (editor: unknown, output: TiptapOutput) => void;
  /** Upload an image file, returns the hosted URL. */
  readonly uploadImage: (image: File) => Promise<string>;
  /** Fetch an image for display (base64 conversion for internal images). */
  readonly fetchImage: (srcUrl: string) => Promise<string>;
  /** Link validation — returns true if allowed. Sets validationError on failure. */
  readonly validateLink: (link: string) => Promise<boolean>;
  /** Video validation — returns true if allowed. Sets validationError on failure. */
  readonly validateVideo: (video: string) => Promise<boolean>;
}

/**
 * ViewModel hook for SectionEditorControl.
 *
 * Handles service interactions (ContentEditorService, MplServiceClient)
 * and exposes a clean interface for the View to render.
 */
export function useSectionEditorControlViewModel(
  node: ContentNode,
  contentId: string,
  isReadonly: boolean,
): SectionEditorControlViewModel {
  const service = useContentEditorService();
  const mplService = useMplServiceClient();
  const flushCoordinator = usePendingFlushCoordinator();
  const entities = useEntities();

  const [validationError, setValidationError] = useState<string | undefined>(undefined);

  // Compute initial content ONCE — prevents TiptapEditor re-mount on every keystroke.
  const initialContent = useMemo<TiptapOutputType>(() => {
    const data = node.contentData;
    return typeof data === 'string' && !isReadonly ? transformTiptapHtml(data) : data;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearValidationError = useCallback(() => setValidationError(undefined), []);

  // Latest pending output from the editor; debounced flush reads from this ref so
  // we don't allocate a new debounced function for every keystroke.
  const pendingOutputRef = useRef<TiptapOutput | undefined>(undefined);

  const flushStageEdit = useCallback(() => {
    const output = pendingOutputRef.current;
    if (!output) return;
    pendingOutputRef.current = undefined;

    const content = service.content;
    if (!content) return;

    const updated = deepCloneContent(content);
    const targetNode = findNodeById(node.id, [updated.contentData]);
    if (!targetNode) {
      log.warn(
        `SectionEditorControlViewModel: node "${node.id}" not found in content tree; dropping edit.`,
      );
      return;
    }
    targetNode[ContentSectionKey.ContentData] = sanitizeCodeBlock(output.json);
    service.stageEdit(updated);
  }, [service, node.id]);

  const debouncedStageEdit = useMemo(
    () => _.debounce(flushStageEdit, STAGE_EDIT_DEBOUNCE_MS),
    [flushStageEdit],
  );

  // Flush any pending edit on unmount so in-flight keystrokes aren't dropped
  // when the user navigates away mid-typing. Callers (e.g. GuideEditor) key
  // sections by id, so a node.id change remounts this hook and runs cleanup.
  useEffect(() => {
    return () => {
      debouncedStageEdit.flush();
    };
  }, [debouncedStageEdit]);

  // While mounted, let a browser-close drain stage the pending keystroke too.
  useEffect(() => {
    return flushCoordinator.registerFlusher(() => debouncedStageEdit.flush());
  }, [flushCoordinator, debouncedStageEdit]);

  const handleContentChanged = useCallback(
    (_editor: unknown, output: TiptapOutput) => {
      pendingOutputRef.current = output;
      debouncedStageEdit();
    },
    [debouncedStageEdit],
  );

  const uploadImage = useCallback(
    async (image: File): Promise<string> => {
      // Take the lock first because the server accepts uploads only from the lock holder.
      // Show failures here and rethrow, since the editor package only logs them.
      try {
        await service.acquireLockIfNeeded();
      } catch (error) {
        setValidationError(
          error instanceof SaveDraftError
            ? error.message
            : 'Failed to take the edit lock. Please try again.',
        );
        throw error;
      }

      let response;
      try {
        response = await mplService.putContentImage(contentId, image);
      } catch (error) {
        setValidationError(
          error instanceof ServiceCallError
            ? `Failed to upload the image: ${error.message}`
            : 'Failed to upload the image. Please try again.',
        );
        throw error;
      }
      return response.data.link;
    },
    [service, mplService, contentId],
  );

  const fetchImage = useCallback(
    async (srcUrl: string): Promise<string> => fetchImageInBase64Format(srcUrl, mplService),
    [mplService],
  );

  const validateLink = useCallback(async (link: string): Promise<boolean> => {
    if (link.startsWith('/') || link.startsWith('#')) return true;
    if (!isURL(link) || !link.toLowerCase().startsWith('https://')) {
      setValidationError('Could not insert link. Your URL is invalid or not using HTTPS.');
      return false;
    }
    return true;
  }, []);

  const validateVideo = useCallback(async (video: string): Promise<boolean> => {
    if (!video) return false;
    if (!ALLOWED_VIDEO_SOURCES.some((s) => video.toLowerCase().includes(s))) {
      setValidationError(
        'Could not insert video. Please provide YouTube URLs in the format youtube.com/watch?v=...',
      );
      return false;
    }
    return true;
  }, []);

  // Figure out what inline edits to display by looking at the nodeType:
  // notes: useMemo to prevent tiptap from unnecessary re-rendering
  const inlineTextFilter: InlineTextFilter = useMemo(
    () => ({ type: 'section', sectionId: node.id, nodeType: node.nodeType }),
    [node.nodeType, node.id],
  );

  return {
    initialContent,
    isReadonly,
    entities,
    validationError,
    clearValidationError,
    handleContentChanged,
    uploadImage,
    fetchImage,
    validateLink,
    validateVideo,
    inlineTextFilter,
  };
}
